import React, { useEffect , useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {doc, getDoc} from "firebase/firestore"
import LatexText from "../components/LatexText";
import { db } from '../firebase-config';

const ProblemPage = ( {Std} ) =>
{
  const { problemId } = useParams();

  const [selectedOptions, setSelectedOptions] = useState([]);
  const [checked, setChecked] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [problemData, setProblemData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect (()=>
  {
    const fetchProblem = async() =>
    {
      try
      {
        const docRef = doc(db,"ProblemSet",Std.toString(),"MCQs",problemId.toString());
        const Problem = await getDoc(docRef);
        if (Problem.exists())
        {
          const Data = Problem.data();
          const options =
          [
            Data.OptionA,
            Data.OptionB,
            Data.OptionC,
            Data.OptionD
          ];

          const letterToIndex = { A: 0, B: 1, C: 2, D: 3 };

          const correctAnswerIndices = Data.CorrectAnswer.map((ch) => letterToIndex[ch]);

          setProblemData({
            ...Data,
            options,
            CorrectAnswer: correctAnswerIndices
          });
        }
      } catch (err)
      {
        console.error("Error fetching the problem:",err);
      }
      setLoading(false);
    }
    fetchProblem();
  }, [problemId]);

  if (loading)
  {
    return <div className="p-10 text-center text-2xl">Loading...</div>;
  }

  if (!problemData)
  {
    return (
      <div className="p-10 text-center">
        <h2 className="text-3xl font-bold text-red-600">Problem not found!</h2>
        <Link to="/problemset" className="text-blue-600 underline text-xl mt-4 block">
          ← Back to Problem Set
        </Link>
      </div>
    );
  }

  const handleSelect = (index) =>
  {
    if (checked) return; // disable changing after checking
    setSelectedOptions((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  const handleCheckAnswers = () =>
  {
    setChecked(true);
  };

  const isCorrect = (index) => problemData.CorrectAnswer.includes(index);
  const userGotItRight =
    selectedOptions.length === problemData.CorrectAnswer.length &&
    selectedOptions.every((i) => problemData.CorrectAnswer.includes(i));

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl border border-black p-8 mt-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-extrabold text-gray-800">
          {problemData.id}. {problemData.Qname}
        </h2>
        <span
          className={`inline-flex items-center px-4 py-1.5 rounded-full font-bold text-xl`}
        >
          Diff: {problemData.Difficulty}
        </span>
      </div>

      {
        problemData.questionImage && (
          <img
              src={problemData.questionImage}
              alt="Question Image"
              className="max-h-[400px] mx-auto mb-4 rounded-lg border"
              loading="lazy"
          />
        )
      }

      <p className="text-lg text-gray-800 mb-8">
        <LatexText text={problemData.Question} />
      </p>

      <div className="space-y-4 mb-8">
        {problemData.options.map((opt, index) =>
        {
          const selected = selectedOptions.includes(index);
          let optionClass =
            'border-2 rounded-xl px-5 py-3 cursor-pointer text-lg font-medium transition-colors duration-150';
          if (checked)
          {
            if (selected && isCorrect(index)) optionClass += ' border-green-500 bg-green-100';
            else if (selected && !isCorrect(index)) optionClass += ' border-red-500 bg-red-100';
            else if (!selected && isCorrect(index)) optionClass += ' border-green-300';
            else optionClass += ' border-gray-300';
          } else
          {
            optionClass += selected
              ? ' border-blue-500 bg-blue-50'
              : ' border-gray-300 hover:bg-gray-100';
          }

          return (
            <div
              key={index}
              onClick={() => handleSelect(index)}
              className={optionClass}
            >
              <LatexText text={opt} />
            </div>
          );
        })}
      </div>

      {!checked ? (
        <button
          onClick={handleCheckAnswers}
          className="text-white bg-blue-600 hover:bg-blue-700 font-semibold py-2 px-6 rounded-lg transition-transform transform hover:scale-105"
        >
          Check Answers
        </button>
      ) : (
        <div className="mt-4">
          <p
            className={`text-xl font-bold ${
              userGotItRight ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {userGotItRight
              ? 'Correct, Well done.'
              : 'Some answers are incorrect.'}
          </p>

          <button
            onClick={() => setShowExplanation((prev) => !prev)}
            className="mt-4 text-blue-600 underline text-lg"
          >
            {showExplanation ? 'Hide Explanation' : 'Show Explanation'}
          </button>

          {showExplanation && (
            <div className="mt-3 p-4 bg-gray-50 border-l-4 border-blue-400 rounded-lg text-gray-800 text-lg">
              <LatexText text={problemData.Explanation} />
            </div>
          )}

          <Link
            to="/problemset"
            className="block mt-8 text-white bg-gray-800 hover:bg-gray-900 font-semibold py-2 px-6 rounded-lg text-center"
          >
            ← Back to Problem Set
          </Link>
        </div>
      )}
    </div>
  );
};

export default ProblemPage;